// TickerContextDetails.jsx
import React from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Typography, Box, Chip, Stack, Divider, List, ListItem, ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MarketBackdrop from './MarketBackdrop';   // DAS-2832

const kv = (label, value) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mr: 2 }}>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}:</Typography>
    <Typography variant="body2">{value ?? 'n/a'}</Typography>
  </Box>
);

/* ---------- color helpers ---------- */
const paletteToken = (k) =>
  ({
    default: 'text.primary',
    success: 'success.main',
    warning: 'warning.main',
    error: 'error.main',
    info: 'info.main',
    primary: 'primary.main',
    secondary: 'secondary.main',
  }[k] || 'text.primary');

const colorForFundamentalLabel = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('strong improvement')) return 'success';
  if (s.includes('moderate improvement')) return 'success';
  if (s.includes('mild improvement')) return 'info';
  if (s.includes('neutral')) return 'info';
  if (s.includes('strong deterioration')) return 'error';
  if (s.includes('moderate deterioration')) return 'warning';
  if (s.includes('mild deterioration')) return 'warning';
  return 'default';
};

const colorForBullBear = (txt) => {
  const s = String(txt || '').toLowerCase();
  if (s.includes('strong sell')) return 'error';
  if (s.includes('weak sell')) return 'warning';
  if (s.includes('sell')) return 'error';

  if (s.includes('strong buy') || s.includes('strong bullish')) return 'success';
  if (s.includes('weak buy')) return 'info';
  if (s.includes('buy') || s.includes('bullish')) return 'success';

  if (s.includes('bearish')) return 'error';
  if (s.includes('neutral')) return 'info';

  if (s.includes('strength')) return 'success'; // e.g., "Fundamental Strength"
  return 'default';
};

export default function TickerContextDetails({ context, alert }) {
  if (!context) return null;

  const st = context.sectorTrendMapped || {};
  const perf = context.performance || {};
  const instArr = context.institutionalSectorTrendsArray || [];
  const tickerVal = context.ticker || 'n/a';
  const sectorVal = context.sector || 'n/a';

  const fundamentalColor = colorForFundamentalLabel(perf.fundamental_trend_label);
  const consensusTrendColor = colorForBullBear(perf.consensus_trend_direction);
  const sectorTrendColor = colorForBullBear(st.trend_direction);

  return (
    <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="context-details-toggle">
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Context Details</Typography>
      </AccordionSummary>
      <AccordionDetails>

        {/* 0) DAS-2832: Market backdrop — the top-down read, FIRST. The blocks below
            are all about the company itself; this one is about the market around it,
            and it is the piece that actually moved the urgency score. Renders nothing
            when nothing in the Broad Picture matched this holding. */}
        <MarketBackdrop alert={alert} sector={sectorVal !== 'n/a' ? sectorVal : null} />

        {/* 1) Fundamental Trend Analysis (TOP) */}
{perf.fundamental_trend_analysis_text && (
  <>
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
    >
      {tickerVal} Fundamental Trend Analysis:
      <Typography
        component="span"
        variant="subtitle1"
        sx={{ fontWeight: 700, color: paletteToken(fundamentalColor) }}
      >
        {perf.fundamental_trend_label || 'Analysis'}
      </Typography>
    </Typography>
    <List dense sx={{ mt: 0 }}>
      <ListItem>
        <ListItemText
          secondary={
            <Typography variant="body2" whiteSpace="pre-line">
              {perf.fundamental_trend_analysis_text}
            </Typography>
          }
        />
      </ListItem>
    </List>
    <Stack direction="row" sx={{ flexWrap: 'wrap', mb: 2 }}>
      {kv('Score', perf.fundamental_trend_score)}
      {kv('Updated', perf.fundamental_trend_updated_at)}
      {kv('Validity (days)', perf.fundamental_trend_validity_days)}
    </Stack>
    <Divider sx={{ mb: 2 }} />
  </>
)}


        {/* 2) Performance and Trend Consensus */}
        {/* 2) Performance and Trend Consensus */}
{Object.keys(perf).length > 0 && (
  <>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      {tickerVal} Performance and Trend Consensus:
      <Typography
        component="span"
        variant="subtitle1"
        sx={{ fontWeight: 700, color: paletteToken(consensusTrendColor) }}
      >
        {perf.consensus_trend_direction ?? 'n/a'}
      </Typography>
    </Typography>


            {/* Performance snapshot */}
            <Stack direction="row" sx={{ flexWrap: 'wrap', mb: 1 }}>
              {kv('YTD %', perf.YTD)}
              {kv('1D %', perf.one_day_perf)}
              {kv('1W %', perf.one_week_performance)}
              {kv('1M %', perf.one_month_performance)}
              {kv('6M %', perf.six_month_performance)}
            {/* </Stack>
            <Stack direction="row" sx={{ flexWrap: 'wrap', mb: 1 }}> */}
              {kv('Expected Return %', perf.expected_return)}
              {kv('Risk-based Req. Return %', perf.Risk_based_required_return)}
              {kv('Risk/Return Gap', perf.risk_return_gap)}
              {kv('Sharpe', perf.sharpe_ratio)}
            </Stack>

            {/* Consensus */}
            <Stack direction="row" sx={{ flexWrap: 'wrap', mb: 1, alignItems: 'center', gap: 1 }}>
              {kv('Consensus Score', perf.consensus_score)}
              {kv('Consensus Strength', perf.consensus_strength)}
              {/* <Chip
                size="small"
                label={`Consensus Trend: ${perf.consensus_trend_direction ?? 'n/a'}`}
                color={consensusTrendColor === 'default' ? 'default' : consensusTrendColor}
              /> */}
              {kv('Confidence', perf.consensus_confidence)}
              {kv('Updated', perf.consensus_updated_at)}
            </Stack>

            {/* Sentiment (optional) */}
            {(perf.sentiment_label || perf.sentiment_score) && (
              <Stack direction="row" sx={{ flexWrap: 'wrap', mb: 1 }}>
                <Chip
                  size="small"
                  label={`Sentiment: ${perf.sentiment_label ?? 'n/a'}`}
                  color={colorForBullBear(perf.sentiment_label) === 'default' ? 'default' : colorForBullBear(perf.sentiment_label)}
                />
                {kv('Sentiment Score', perf.sentiment_score)}
                {kv('Sentiment Validity (d)', perf.sentiment_validity_days)}
                {kv('Sentiment Updated', perf.sentiment_updated_at)}
              </Stack>
            )}

            <Divider sx={{ mb: 2 }} />
          </>
        )}

        {/* 3) Sector Trend */}
        {(context.sector || st.trend_direction || st.trend_confidence) && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {sectorVal} Sector Trend
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              {/* <Chip label={`Sector: ${sectorVal}`} color="info" size="small" /> */}
              <Chip
                size="small"
                label={`Trend: ${st.trend_direction ?? 'n/a'}`}
                color={sectorTrendColor === 'default' ? 'default' : sectorTrendColor}
              />
              {st.trend_confidence && <Chip label={`Confidence: ${st.trend_confidence}`} size="small" />}
              {st.trend_consensus_score && <Chip label={`Score: ${st.trend_consensus_score}`} size="small" />}
            </Stack>
            <Divider sx={{ mb: 2 }} />
          </>
        )}

        {/* 4) Institutional Sector Trend */}
        {instArr.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {sectorVal} Institutional Trend
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
              {instArr.map(({ key, region, value }) => (
                <Chip
                  key={key}
                  label={`${region}: ${value}`}
                  size="small"
                  color={colorForBullBear(value) === 'default' ? 'default' : colorForBullBear(value)}
                />
              ))}
            </Stack>
          </>
        )}

      </AccordionDetails>
    </Accordion>
  );
}
